"use client";

import React, { Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { useNav } from "../components/NavigationProvider";
import { PORTAL_THEME } from "../../lib/portal_theme";
import { COMPOSITOR_DEST, COMPOSITOR_PRICE, FULL_SERVICE_PRICE } from "../../lib/full_service_fields.js";
import { nilModeFromCookie } from "../../lib/nil_fields.js";
import { SUBSCRIPTION_PRICE } from "../../lib/subscription_fields.js";
import useSubscription from "../components/useSubscription";

const T = PORTAL_THEME;

const CARD = {
  flex: "1 1 260px", maxWidth: 360, cursor: "pointer", textAlign: "left",
  border: T.cardEdge, boxShadow: "none",
  borderRadius: 0, background: T.cardBackground, padding: "22px 20px", color: T.text,
  fontFamily: T.fontFamily, display: "flex", flexDirection: "column", gap: 10,
};

// Service choice — right after sign-in (/start/<entry> → /signin → here).
// Compositor continues to the flow's template selection exactly as before;
// Full Service goes to the artist order form; Subscription goes to Shopify
// checkout for the monthly plan (lib/subscription.js). A subscriber sees
// Instant Graphics as Included and the Subscription card as already theirs.
export default function ServicePage() {
  return <Suspense><ServicePageInner /></Suspense>;
}

function ServicePageInner() {
  const nav = useNav();
  const searchParams = useSearchParams();
  const flow = searchParams.get("flow") === "print" ? "print" : "announcement";
  // NIL stores (app/nil/route.js): Instant is free — the athlete sells, not buys.
  const [nil, setNil] = React.useState(false);
  React.useEffect(() => { setNil(flow === "print" && (searchParams.get("nil") === "1" || nilModeFromCookie())); }, [flow, searchParams]);
  const sub = useSubscription();
  const subscribed = sub.loaded && sub.subscribed;
  const [subscribing, setSubscribing] = React.useState(false);
  const [subError, setSubError] = React.useState("");

  const subscribe = async () => {
    if (subscribing) return;
    const signIn = () => { window.location.href = `/signin?noGuest=1&returnTo=${encodeURIComponent(`/service?flow=${flow}`)}`; };
    if (sub.loaded && !sub.signedIn) return signIn();
    setSubscribing(true); setSubError("");
    try {
      const res = await fetch("/api/subscription", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "subscribe" }),
      });
      if (res.status === 401) return signIn();
      const d = await res.json().catch(() => ({}));
      if (d?.error === "already_subscribed") { nav.push("/subscription"); return; }
      if (!res.ok || !d?.checkoutUrl) throw new Error(d?.error === "subscription_not_configured"
        ? "Subscriptions aren't available yet — please check back soon." : (d?.error || `Failed (${res.status})`));
      window.location.href = d.checkoutUrl;
    } catch (e) { setSubError(String(e?.message || e)); setSubscribing(false); }
  };

  const option = ({ title, price, blurb, points, cta, note, muted, onClick }) => (
    <button type="button" style={CARD} onClick={onClick}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{title}</h2>
        <span style={{ fontSize: 20, fontWeight: 800 }}>{price}</span>
      </div>
      <p style={{ color: T.textMuted, fontSize: 13, lineHeight: 1.6, margin: 0 }}>{blurb}</p>
      <ul style={{ margin: 0, paddingLeft: 18, color: T.text, fontSize: 13, lineHeight: 1.8 }}>
        {points.map((p) => <li key={p}>{p}</li>)}
      </ul>
      <span style={{
        marginTop: 6, padding: "11px 0", borderRadius: 0, textAlign: "center",
        background: T.chromeGradient, textTransform: "uppercase", letterSpacing: "1px", border: T.chromeBorder, boxShadow: T.chromeButtonShadow,
        color: T.chromeButtonText, fontWeight: 700, fontSize: 14,
        ...(muted ? { background: "transparent", border: `1px solid ${T.cardBorder}`, boxShadow: "none", color: T.text } : {}),
      }}>{cta}</span>
      {note && <span style={{ color: T.textMuted, fontSize: 12, textAlign: "center" }}>{note}</span>}
    </button>
  );

  return (
    <div style={{ minHeight: "100vh", fontFamily: T.fontFamily, color: T.text }}>
      <div style={{ maxWidth: nil ? 820 : 1180, margin: "0 auto", padding: "48px 20px" }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, margin: "0 0 6px", letterSpacing: "-0.5px", textAlign: "center" }}>How would you like your graphic made?</h1>
        <p style={{ color: T.textMuted, fontSize: 14, margin: "0 0 28px", textAlign: "center" }}>Build it yourself in seconds, or have one of our artists make it for you.</p>

        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "stretch", justifyContent: "center" }}>
          {option({
            title: "Instant Graphics",
            price: nil ? "Free" : subscribed ? "Included" : COMPOSITOR_PRICE,
            blurb: "Use our compositor to build your graphic yourself.",
            points: ["Pick a template and background", "Upload your photo and logo", "See it instantly and download right away"],
            cta: "Start Designing →",
            onClick: () => nav.push(COMPOSITOR_DEST[flow]),
          })}
          {option({
            title: "Full Service",
            price: FULL_SERVICE_PRICE,
            blurb: "Tell us what you want and an artist designs it for you.",
            points: ["Send up to 3 photos and 5 examples", "Describe your vision — uniform swap available", "Your graphic delivered within 24 hours"],
            cta: "Fill Out Order Form →",
            onClick: () => nav.push(`/full-service?flow=${flow}`),
          })}
          {/* NIL athletes already design for free. */}
          {!nil && option(subscribed ? {
            title: "Subscription",
            price: "",
            blurb: "Unlimited Instant Graphics for prints and announcements.",
            points: ["Unlimited announcement videos and graphics", "Unlimited custom print images", "Watermark-free downloads, no checkout"],
            cta: "You are already a subscriber.",
            note: sub.status === "cancelled" && sub.endsAt
              ? `Cancelled — included until ${new Date(sub.endsAt).toLocaleDateString()}`
              : "Manage or cancel your subscription",
            muted: true,
            onClick: () => nav.push("/subscription"),
          } : {
            title: "Subscription",
            price: <>{SUBSCRIPTION_PRICE}<span style={{ fontSize: 13, fontWeight: 600, color: T.textMuted }}>/mo</span></>,
            blurb: "Unlimited Instant Graphics for prints and announcements.",
            points: ["Unlimited announcement videos and graphics", "Unlimited custom print images", "Cancel any time"],
            cta: subscribing ? "Redirecting to checkout…" : "Subscribe →",
            onClick: subscribe,
          })}
        </div>
        {subError && <p style={{ color: T.error, fontSize: 13, margin: "14px 0 0" }}>{subError}</p>}
      </div>
    </div>
  );
}
